import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  HeaderContainer,
  HeaderContent,
  Logo,
  Nav,
  NavList,
  NavItem,
  NavLink,
  DropdownMenu,
  DropdownItem,
  DropdownLink,
  MobileMenuButton,
} from '../styles/HeaderStyles';
import Base64Image from './Base64Image';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';

const CACHE_KEY = 'home_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const isActive = (path) => (location.pathname === path ? 'active' : '');

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (
        cached &&
        cached.data &&
        cached.data.header
      ) {
        if (!isMounted) return;
        setHeaderData(cached.data.header);
      }

      try {
        if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
          return;
        }

        const data = await get('/home');
        if (!isMounted) return;

        if (data && data.header) {
          setHeaderData(data.header);
        }

        setCachedData(CACHE_KEY, data);
      } catch (err) {
        // Header can stay without data if request fails; Home page handles error display.
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo>
          {headerData && headerData.logo && (
            <Base64Image
              image={headerData.logo}
              alt="Logo"
              style={{ height: 50, width: 'auto', objectFit: 'contain' }}
            />
          )}
          <div>
            {headerData ? headerData.organizationName : ''}
          </div>
        </Logo>

        <Nav isOpen={isMobileMenuOpen}>
          <NavList>
            <NavItem>
              <NavLink to="/" className={isActive('/')}>Home</NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="/about"
                className={`${isActive('/about')} has-dropdown`.trim()}
              >
                About
              </NavLink>
              <DropdownMenu>
                <DropdownItem>
                  <DropdownLink to="/about" className={isActive('/about')}>
                    Church
                  </DropdownLink>
                </DropdownItem>
                <DropdownItem>
                  <DropdownLink
                    to="/buildingproject"
                    className={isActive('/buildingproject')}
                  >
                    Building Project
                  </DropdownLink>
                </DropdownItem>
                <DropdownItem>
                  <DropdownLink to="/gallery" className={isActive('/gallery')}>
                    Gallery
                  </DropdownLink>
                </DropdownItem>
                <DropdownItem>
                  <DropdownLink
                    to="/churchactivities"
                    className={isActive('/churchactivities')}
                  >
                    Church Activities
                  </DropdownLink>
                </DropdownItem>
                <DropdownItem>
                  <DropdownLink
                    to="/testimonies"
                    className={isActive('/testimonies')}
                  >
                    Testimonies
                  </DropdownLink>
                </DropdownItem>
                <DropdownItem>
                  <DropdownLink to="/contact" className={isActive('/contact')}>
                    Contact
                  </DropdownLink>
                </DropdownItem>
              </DropdownMenu>
            </NavItem>
            <NavItem>
              <NavLink to="/school" className={isActive('/school')}>
                School
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/clinic" className={isActive('/clinic')}>
                Clinic
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/rehab" className={isActive('/rehab')}>
                Rehab
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink to="/donate" className={isActive('/donate')}>
                Donate
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                to="/actsFellowship"
                className={`${isActive('/actsFellowship')} has-dropdown`.trim()}
              >
                Ministry
              </NavLink>
              <DropdownMenu>
                <DropdownItem>
                  <DropdownLink
                    to="/actsFellowship"
                    className={isActive('/actsFellowship')}
                  >
                    Acts Fellowship
                  </DropdownLink>
                </DropdownItem>
                <DropdownItem>
                  <DropdownLink
                    to="/training"
                    className={isActive('/training')}
                  >
                    Resources
                  </DropdownLink>
                </DropdownItem>
              </DropdownMenu>
            </NavItem>
          </NavList>
        </Nav>

        <MobileMenuButton onClick={toggleMobileMenu}>
          Menu
        </MobileMenuButton>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
